import React, { useMemo, useState } from 'react'
import {
	Box, Button, Chip, IconButton, Typography, useTheme,
} from '@mui/material'
import { AppNote, secondaryButtonSx } from '@components'
import { dsColorNew } from '@palette'
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Schedule as ScheduleIcon, EmojiEvents as EmojiEventsIcon } from 'Pages/DsRepricing/@components/icons'
import OfficeSlicer    from 'Pages/MyBusiness/_shared/Filters/OfficeSlicer'
import CategorySlicer  from 'Pages/MyBusiness/_shared/Filters/CategorySlicer'

import {
	evaluateDay, ruleHoursForDay, WEEKDAY_LABELS,
} from '../mockData'
import { useExecutionsByDay, useScheduleRules, useStrategyFeed } from '../@services/useSchedule'
import Cell     from './Cell'
import Popover  from './Popover'
import ChangesDialog from '../@components/ChangesDialog'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { displayDay } from '@utils'
import { format } from 'date-fns'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'

/**
 * Month calendar for the Repricing Schedule.
 *
 * Each cell = a pickup date. Renders TWO stacks:
 *   1. Applicable schedule rules — one per active scope. Multiple rules
 *      can show up when they cover the same day for different offices/
 *      acriss and the calendar filter is broader than any single rule.
 *   2. Applicable strategies, ordered by priority. Non-applicable
 *      strategies are surfaced with a "+N do not apply" chip; details
 *      in the popover with the reason each was rejected.
 *
 * The calendar filter (Office / ACRISS) intersects with each rule's
 * and strategy's own scope filters.
 */
export default function Calendar() {
	const theme = useTheme()
	const { rules, loading: rulesLoading } = useScheduleRules()
	// Real strategies, or none and a notice. Five invented ones next to
	// real rules would be worse than an honest gap (brief criterio 21).
	const { strategies, unavailable: strategiesUnavailable } = useStrategyFeed()

	const [monthAnchor, setMonthAnchor] = useState(startOfMonth(new Date()))
	const [officeFilter, setOfficeFilter] = useState([])
	const [sippFilter, setSippFilter] = useState([])
	const [selectedDay, setSelectedDay] = useState(null)
	// A clicked figure of a past day: `{ dayIso, figure }` behind the popup.
	const [changes, setChanges] = useState(null)

	const today = useMemo(() => stripTime(new Date()), [])

	const grid = useMemo(
		() => buildMonthGrid(monthAnchor),
		[monthAnchor],
	)
	// Past days show what ran instead of what would apply: a pickup
	// date that has gone by has no schedule to speak of (Curro,
	// 2026-10-03). One request per visible month.
	const days = grid.filter(Boolean)
	const ran = useExecutionsByDay(days[0]?.iso, days[days.length - 1]?.iso)

	const evalByDate = useMemo(() => {
		const out = new Map()
		for (const cell of grid) {
			if (!cell) continue
			const ev = evaluateDay({
				pickupDate: cell.date, today,
				rules, strategies,
				officeFilter, sippFilter,
			})
			out.set(cell.iso, ev)
		}
		return out
	}, [grid, rules, strategies, officeFilter, sippFilter, today])

	const summary = useMemo(() => computeSummary(grid, evalByDate, rules, strategies), [grid, evalByDate, rules, strategies])

	const shift = (n) => {
		const d = new Date(monthAnchor)
		d.setMonth(d.getMonth() + n)
		setMonthAnchor(startOfMonth(d))
		setSelectedDay(null)
	}

	return (
		<Box>
			{/* Header */}
			<Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap"
			     sx={{ mb: 2 }}>
				<IconButton size="small" onClick={() => shift(-1)} sx={navButtonSx}>
					<ChevronLeftIcon/>
				</IconButton>
				<Typography sx={{ minWidth: 200, fontSize: 18, fontWeight: 600, color: dsColorNew.textTitle,
				                  textAlign: 'center', textTransform: 'capitalize' }}>
					{monthAnchor.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}
				</Typography>
				<IconButton size="small" onClick={() => shift(1)} sx={navButtonSx}>
					<ChevronRightIcon/>
				</IconButton>
				<Button size="small" variant="outlined" sx={{ ...secondaryButtonSx, height: 36 }}
				        onClick={() => setMonthAnchor(startOfMonth(new Date()))}
				        disabled={sameMonth(monthAnchor, new Date())}>
					Today
				</Button>
				<Box flex={1}/>
				<OfficeSlicer   value={officeFilter} onChange={setOfficeFilter}/>
				<CategorySlicer value={sippFilter}   onChange={setSippFilter}/>
			</Box>

			{/* Summary banner */}
			<Summary summary={summary} theme={theme}
			         strategiesUnavailable={strategiesUnavailable}
			         rulesLoading={rulesLoading}/>

			{/* Grid */}
			{/* `minmax(0, 1fr)`: a long rule name ellipsises inside its day instead of
			    widening the column and pushing the week off the card. */}
			<Box sx={{ overflow: 'hidden', mt: 2, borderRadius: '12px', border: `1px solid ${dsColorNew.grayBorder}` }}>
				<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
				           bgcolor: dsColorNew.tableHead }}>
					{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(l => (
						<Box key={l} sx={{ px: 1, py: 1.25, fontSize: 12,
						                    fontWeight: 600,
						                    color: dsColorNew.white, textAlign: 'center' }}>
							{l}
						</Box>
					))}
				</Box>
				<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
					{grid.map((cell, i) => (
						<Cell key={i} cell={cell}
						      today={today}
						      evaluation={cell ? evalByDate.get(cell.iso) : null}
						      ran={cell ? ran.days[cell.iso] : null}
						      ranUnavailable={ran.unavailable || ran.loading}
						      onOpenChanges={(figure) => setChanges({ dayIso: cell.iso, figure })}
						      selected={cell && selectedDay === cell.iso}
						      onClick={() => cell && setSelectedDay(cell.iso)}/>
					))}
				</Box>
			</Box>

			{/* Popover with detail of the selected day */}
			<Popover
				open={!!selectedDay}
				onClose={() => setSelectedDay(null)}
				dayIso={selectedDay}
				today={today}
				evaluation={selectedDay ? evalByDate.get(selectedDay) : null}
				ran={selectedDay ? ran.days[selectedDay] : null}
				onOpenChanges={(figure) => setChanges({ dayIso: selectedDay, figure })}
			/>
			{changes && changes.figure.query && (
				<ChangesDialog type={changes.figure.query.event_type || null}
				               title={`${format(new Date(changes.dayIso + 'T00:00:00'), 'EEEE')} ${displayDay(changes.dayIso)} · ${changes.figure.label}`}
				               query={{ date_from: changes.dayIso, date_to: changes.dayIso, ...changes.figure.query }}
				               activityHref={`${DS_REPRICING_ROUTES.ACTIVITY_PATH}?${new URLSearchParams({
				                   from: changes.dayIso, to: changes.dayIso, ...changes.figure.query }).toString()}`}
				               activityLabel="Open in Activity, filtered on this day"
				               emptyText="Nothing of this kind on this day."
				               onClose={() => setChanges(null)}/>
			)}
		</Box>
	)
}

// The month arrows as the app's square header buttons
const navButtonSx = {
	width: 32, height: 32, borderRadius: '8px', border: `1px solid ${dsColorNew.grayBorder}`,
	color: dsColorNew.textMuted, bgcolor: dsColorNew.white,
	'&:hover': { borderColor: dsColorNew.appBlue, color: dsColorNew.appBlue, bgcolor: dsColorNew.hoverBgLight },
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------
function Summary({ summary, theme, strategiesUnavailable, rulesLoading }) {
	const chips = [
		{ icon: <ScheduleIcon    fontSize="small"/>,
		  text: `${summary.rulesLive}/${summary.rulesTotal} rules live`,
		  tone: 'info' },
		{ icon: <EmojiEventsIcon fontSize="small"/>,
		  text: `${summary.stratsLive}/${summary.stratsTotal} strategies live`,
		  tone: 'ok' },
	]
	const warnings = []
	// An unreachable strategies API is said out loud: the "days without an
	// active strategy" count below would otherwise read as a configuration
	// problem when it is really a data problem (brief criterio 21).
	if (strategiesUnavailable) {
		warnings.push('strategies unavailable — the days below only reflect schedule rules')
	}
	if (rulesLoading) warnings.push('loading the schedule…')
	if (summary.daysWithoutRule)
		warnings.push(`${summary.daysWithoutRule} days without a schedule rule`)
	if (summary.daysWithoutStrategy)
		warnings.push(`${summary.daysWithoutStrategy} days without an active strategy`)

	return (
		<Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
			{chips.map((c, i) => (
				<Chip key={i} icon={c.icon} label={c.text} size="small" sx={toneChipSx(c.tone)}/>
			))}
			{warnings.length > 0 && (
				<AppNote severity="warning" sx={{ py: 0.75, flex: 1, minWidth: 240 }}>
					{warnings.join(' · ')}
				</AppNote>
			)}
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Grid + utilities
// ---------------------------------------------------------------------------
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x }
function stripTime(d)    { const x = new Date(d); x.setHours(0,0,0,0); return x }
function sameMonth(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() }

function buildMonthGrid(anchor) {
	const first = startOfMonth(anchor)
	const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
	// Monday-first grid (getDay: 0=Sun … 6=Sat)
	const firstDow = (first.getDay() + 6) % 7
	const cells = []
	for (let i = 0; i < firstDow; i++) cells.push(null)
	for (let d = 1; d <= daysInMonth; d++) {
		const date = new Date(first.getFullYear(), first.getMonth(), d)
		cells.push({ date, iso: isoDate(date), day: d })
	}
	// pad to complete rows of 7
	while (cells.length % 7 !== 0) cells.push(null)
	return cells
}

function isoDate(d) {
	return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function computeSummary(grid, evalByDate, rules, strategies) {
	let daysWithoutRule = 0, daysWithoutStrategy = 0
	for (const cell of grid) {
		if (!cell) continue
		const ev = evalByDate.get(cell.iso)
		if (!ev.applicableRules.length)       daysWithoutRule++
		if (!ev.applicableStrategies.length)  daysWithoutStrategy++
	}
	return {
		rulesTotal: rules.length,
		rulesLive:  rules.filter(r => r.is_active).length,
		stratsTotal: strategies.length,
		stratsLive:  strategies.filter(s => s.is_active).length,
		daysWithoutRule, daysWithoutStrategy,
	}
}
